import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import dateFormat from 'dateformat';
import classnames from 'classnames';
import { TrackingHandler } from 'client/tracking/handler';
import { TopRatedArticleMap } from 'client/tracking/maps/editorial/top-rated';
import { CmsEntities } from 'client/data/models/cms';
import { getCanonical } from 'site-modules/shared/utils/seo-helper';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { MainContent } from 'site-modules/editorial/components/top-rated/main-content/main-content';
import { Share } from 'site-modules/shared/components/share/share';
import { PhotoflipperImageTopRated } from 'site-modules/shared/components/editorial/photoflipper-image-top-rated/photoflipper-image-top-rated';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { buildArticlePageBreadcrumb } from 'site-modules/shared/utils/top-rated/top-rated';
import { topRatedHelpers } from 'site-modules/shared/utils/top-rated/top-rated-helpers';
import { PhotoflipperVideoImageTopRated } from 'site-modules/shared/components/editorial/photoflipper-video-image-top-rated/photoflipper-video-image-top-rated';
import { WinnerTilesNavArticle } from 'site-modules/editorial/components/top-rated/winner-tiles-nav/2026/winner-tiles-nav-article';
import { HeroContent } from 'site-modules/editorial/components/hero-content/2026/hero-content';
import { Finalists } from 'site-modules/editorial/components/top-rated/finalists/2026/finalists';
import { OtherYears } from 'site-modules/editorial/components/top-rated/other-years/other-years';
import { getFullImageUrl } from 'client/utils/image-helpers';
import { AwardsAccolades as TopRatedAwardsAccoladesUI } from 'site-modules/editorial/components/top-rated/awards-accolades/awards-accolades';
import { rendererTopRatedArticle } from 'site-modules/editorial/pages/top-rated/top-rated-article-renderer';
import { OnScreenContentWrapper } from 'client/site-modules/editorial/components/top-rated/main-content/on-screen-content-wrapper';
import { RevealOnIntersect } from 'site-modules/editorial/components/top-rated/reveal-on-intersect/reveal-on-intersect';
import { LeaderboardNative } from 'site-modules/shared/components/native-ad/leaderboard/leaderboard-native';
import { AdUnit } from 'site-modules/shared/components/ad-unit/ad-unit';
import { FeatureFlag } from 'site-modules/shared/components/feature-flag/feature-flag';
import { MarketingButtons } from 'site-modules/editorial/components/top-rated/marketing-buttons/marketing-buttons';
import { AnimatedHoverLinkButton } from 'site-modules/editorial/components/top-rated/hover-button/animated-hover-link-button';
import { LaurelIcons } from 'site-modules/editorial/components/top-rated/icons/laurel-icons';
import { getSectValues } from 'site-modules/shared/utils/top-rated/get-sect-values';
import { getYearToUse } from 'site-modules/shared/utils/top-rated/use-theme';

import './styles/top-rated-article.scss';

const ETR_ADS_PROPS = {
  // desktop
  false: { adName: 'articlebutton', md: true, lg: true, xl: true },
  // mobile
  true: { adName: 'marticlebutton', xs: true, sm: true },
};

const CATEGORY_WINNER_CLICK_TAG = {
  // CATEGORY WINNER
  // ADS-12132: Rivian ETR sponsorship — wired article button only, no BOTB variant
  'electric-truck': {
    false:
      // eslint-disable-next-line no-template-curly-in-string
      'https://ad.doubleclick.net/ddm/trackclk/N2429713.2990EDMUNDS.COMINC/B35285991.451960926;dc_trk_aid=645428982;dc_trk_cid=259551328;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};ltd=;dc_tdv=1',
    true: null,
  },
};

const INLINE_AD_CTA_COPY = {
  car: {
    ctaText: 'Shop the Honda Civic Hybrid',
    clickOutUrl: 'ShopHonda.com',
  },
  'electric-truck': {
    ctaText: 'Shop the 2026 Rivian R1T',
    clickOutUrl: 'ShopRivian.com',
  },
};

const BOTB_CONTENT_ID = 'botb-conent';

const FINALISTS_TITLE = 'HIGHLY RECOMMENDED';

export function TopRatedArticleUI({
  content,
  pathname = null,
  isMobile = false,
  year,
  isDefaultYear = false,
  type = null,
  themeContent = null,
}) {
  useEffect(() => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    TrackingHandler.useMap(TopRatedArticleMap);
  }, []);
  const { published, updated, title } = content;
  const canonical = getCanonical(content);
  const mainEntries = content.child('main').children();
  const marketingButtonEntry = content.child('marketingButton');

  // hero video
  const heroContentEntry = content.child('hero-content');

  // breadCrumb
  const breadcrumbTitle = heroContentEntry.metadata('breadcrumbTitle').value();
  const pageTitleToUse = breadcrumbTitle || title;
  const breadcrumbsData = buildArticlePageBreadcrumb(isDefaultYear ? '' : year, pageTitleToUse, pathname);

  const mainEntryData = mainEntries.reduce((acc, cur) => {
    if (cur.id === 'vehicle-details') {
      acc.vehicleDetails = cur;
    } else if (cur.id === 'vehicle-tiles') {
      acc.vehicleTiles = cur.links();
    } else if (cur.id === 'winners-list') {
      acc.winnersList = cur;
    } else if (cur.id === 'config') {
      acc.config = cur;
    } else if (cur.id === 'featured-video-images') {
      const imageLinks = cur.links();
      const videoId = cur.metadata('videoId').value();
      const videoTitle = cur.metadata('videoTitle').value();
      const href = cur.metadata('videoThumbnailURL').value();
      const videoThumbOnly = cur.metadata('videoThumbnailOnly').value() === 'true';
      const videoAspectRatio = cur.metadata('videoThumbnailAspectRatio').value();
      acc.featuredVideoImages = [
        { videoId, videoTitle, href, title: videoTitle, videoThumbOnly, videoAspectRatio },
        ...imageLinks,
      ];
    } else if (cur.id === 'by-the-numbers') {
      acc.byTheNumbers = cur;
    } else if (cur.id === 'finalists') {
      acc.finalists = cur;
    } else if (cur.id === 'best-of-the-best-teaser') {
      acc.bestOfTheBestTeaser = cur;
    } else if (cur.id === 'best-of-the-best-description') {
      acc.bestOfTheBestDescription = cur;
    } else if (cur.id === 'roadtest-images') {
      const imageLinks = cur.links();
      const videoId = cur.metadata('videoId').value();
      const videoTitle = cur.metadata('videoTitle').value();
      const href = cur.metadata('videoThumbnailURL').value();
      const videoThumbOnly = cur.metadata('videoThumbnailOnly').value() === 'true';
      const videoAspectRatio = cur.metadata('videoThumbnailAspectRatio').value();
      acc.roadtestImages = [
        { videoId, videoTitle, href, title: videoTitle, videoThumbOnly, videoAspectRatio },
        ...imageLinks,
      ];
    }

    return acc;
  }, {});

  const vehicleDetails = mainEntryData.vehicleDetails || null;
  const vehicleTitle = vehicleDetails && vehicleDetails.metadata('vehicletitle').value();
  const vehicleSubtitle = vehicleDetails && vehicleDetails.metadata('subtitle').value();
  const vehicleDate = vehicleDetails && vehicleDetails.metadata('date').value();
  const coreLink = vehicleDetails && vehicleDetails.metadata('coreLink').value();
  const coreLinkText = vehicleDetails && vehicleDetails.metadata('coreLinkText').value();

  const vehicleTiles = mainEntryData.vehicleTiles || null;
  const vehicleGallery = vehicleTiles && vehicleTiles.map(tile => ({ href: tile.href, title: tile.captionTranscript }));

  const featuredVideoImages = mainEntryData.featuredVideoImages || null;
  const featuredVideoImageGallery =
    featuredVideoImages &&
    featuredVideoImages.map(media => ({
      videoId: media.videoId,
      videoTitle: media.videoTitle,
      href: media.href,
      title: media.captionTranscript || media.videoTitle,
      videoThumbOnly: media.videoThumbOnly,
      videoAspectRatio: media.videoAspectRatio,
    }));
  const roadtestImages = mainEntryData.roadtestImages || null;
  const featuredRoadtestGallery =
    roadtestImages &&
    roadtestImages.map(media => ({
      videoId: media.videoId,
      videoTitle: media.videoTitle,
      href: media.href,
      title: media.captionTranscript || media.videoTitle,
      videoThumbOnly: media.videoThumbOnly,
      videoAspectRatio: media.videoAspectRatio,
    }));

  const winnersList = mainEntryData.winnersList || null;
  const winners = winnersList && winnersList.child('winners');
  const winnerTitle = winners && winners.metadata('winnerTitle').value();
  const winnerVehicles = winners && winners.children();
  const permissions = mainEntryData.config && mainEntryData.config.child('permissions');
  const fullImageSize = mainEntryData.config && mainEntryData.config.child('fullImageSize');
  const pagePaths = topRatedHelpers.getPagePathFromConfigData(mainEntryData.config);
  const platform = isMobile ? 'mobile' : 'wired';
  const fullImageSizeFromMetadata = fullImageSize && fullImageSize.metadata(platform).value();
  const isBestOfTheBest = !!(pathname && pathname.includes('best-of-the-best'));

  const rawWinnersEntries = winnerVehicles;
  const winnersEntries = topRatedHelpers.mapWinners2023ArticlePage(rawWinnersEntries, pathname, pagePaths);

  const otherYearLinks = content.child('previous-years').links();

  const bestOfTheBestTeaser = mainEntryData.bestOfTheBestTeaser || null;
  const showBestofTheBestTeaser = !!bestOfTheBestTeaser;
  const bestOfTheBestDescription = mainEntryData.bestOfTheBestDescription || null;
  const isBestOfTheBestPage = !!bestOfTheBestDescription;
  const isElectricPage = !!(pathname && pathname.includes('electric'));
  const yearToUse = getYearToUse(themeContent, year);
  const hasInlineAds = !!getSectValues(yearToUse, type);
  const inlineAdsCtaCopy = INLINE_AD_CTA_COPY[type]?.ctaText;
  const inlineAdsCtaClickOutUrl = INLINE_AD_CTA_COPY[type]?.clickOutUrl;

  const getIceElectricTitle = () => {
    if (!isElectricPage) {
      return title;
    }
    const titleSplit = title.split('Electric');
    return (
      <span>
        {titleSplit[0].trim()}
        <span className="electric-text me-0_25">
          <i className="icon-power2" aria-hidden />
          Electric
        </span>
        {titleSplit[1]}
      </span>
    );
  };

  const renderBotbWinnerTitle = () => {
    const botbTitle = 'Best of the Best';
    const edmundsTitleSnippet = title.split(botbTitle)[0].trim();
    const botbVehicleTitle = vehicleTitle.replace(/^\d{4}/, '').trim();
    return (
      <Fragment>
        <h1
          className="is-botb mb-1 mb-md-0"
          style={{
            lineHeight: 'unset',
            fontSize: 'unset',
          }}
        >
          <div className="text-white text-shadow-contrast-high">
            <span className="hidden-md-down size-20 mb-0_25">{`${edmundsTitleSnippet} ${year}`}</span>
            <span className="hidden-md-up">{`${edmundsTitleSnippet} ${year}`}</span>
          </div>
          <div className="px-0_75 pt-0_25 pb-md-0_5 text-yellow-30 fw-bold text-shadow-contrast-high">
            <span className="hidden-md-down size-36">{botbTitle}</span>
            <span className="hidden-md-up size-30 px-0_5">{botbTitle}</span>
          </div>
        </h1>
        <h2 className=" mb-1_5 heading-secondary mx-auto pos-r is-botb" style={{ maxWidth: '648px', height: '116px' }}>
          <LaurelIcons maxWidth="648px" />
          <div
            className="fw-bold px-3_5 px-md-0 mx-2 mx-sm-auto d-flex align-items-center justify-content-center"
            style={{ maxWidth: '490px', height: '116px' }}
          >
            {botbVehicleTitle}
          </div>
        </h2>
      </Fragment>
    );
  };

  const showBotbContent = e => {
    e.preventDefault();
    document.getElementById(BOTB_CONTENT_ID).scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <main id="main-content" tabIndex={-1} role="main" className="top-rated-article mx-auto pos-r">
      <Breadcrumbs className="px-0" data={breadcrumbsData} />
      <HeroContent content={heroContentEntry} showOverlayContent={false} isMobile={isMobile} />
      <section className="article-wrapper px-1 px-lg-0 mx-auto pos-r text-center">
        <RevealOnIntersect>
          <article className="article-top mx-auto pos-r">
            <header className="text-white">
              {!isBestOfTheBest && (
                <Fragment>
                  <h1
                    className={classnames(
                      'fw-medium mb-0_25 mb-md-0_5 heading-primary text-white text-shadow-contrast',
                      {
                        'px-0_75 py-0_5 rounded pos-r d-inline-block': isBestOfTheBest,
                      }
                    )}
                    style={{ WebkitBackgroundClip: 'text' }}
                  >
                    <span className="pos-r">{getIceElectricTitle()}</span>
                  </h1>
                  <h2 className="fw-bold mb-1 mb-md-1_5 heading-secondary text-shadow-contrast">{vehicleTitle}</h2>
                </Fragment>
              )}
              {isBestOfTheBest && renderBotbWinnerTitle()}
              {vehicleSubtitle && (
                <div
                  className={classnames({
                    'mx-auto': isBestOfTheBest,
                  })}
                  style={isBestOfTheBestPage ? { maxWidth: '650px' } : null}
                >
                  <ContentFragment classes="size-16 mb-0_5 mx-auto vehicle-subtitle subheading-secondary">
                    {vehicleSubtitle}
                  </ContentFragment>
                </div>
              )}
              <div className="size-12 mb-1 vehicle-date small-secondary">
                {dateFormat(vehicleDate || updated || published, 'mmmm dS, yyyy')}
              </div>
              <Share
                url={canonical}
                title={title}
                labelClassName="d-none"
                containerClassName={classnames('d-block text-center', {
                  'mb-2_5': !isBestOfTheBest,
                  'mb-0_5': isBestOfTheBest,
                })}
                iconClassName="heading-4 mx-1 p-0 share-link-primary"
              />
            </header>
            {isBestOfTheBest && (
              <div className="mb-1 mb-md-2 botb-arrow-down-anim">
                <a
                  href={`#${BOTB_CONTENT_ID}`}
                  className="icon-arrow-down2 text-white size-20"
                  onClick={showBotbContent}
                >
                  <span className="visually-hidden">Go to Best of the Best content</span>{' '}
                </a>
              </div>
            )}
            <MarketingButtons
              entry={marketingButtonEntry}
              wrapperClass=""
              wrapperStyle={{ paddingBottom: '80px' }}
              fontClass="size-20 text-cool-gray-60"
              isBordered={false}
              isArticlePage
              isElectricPage={isElectricPage}
            />
          </article>
        </RevealOnIntersect>
      </section>
      <section className="article-wrapper mx-auto pos-r px-1 px-lg-0" id={isBestOfTheBest && BOTB_CONTENT_ID}>
        <RevealOnIntersect>
          <article className="gradient-wrapper article-top mx-auto pos-r pb-2_5">
            <section className="pt-1 pt-md-2_5 px-1 px-md-2_5 text-center">
              {showBestofTheBestTeaser && (
                <figure className="teaser-section">
                  <ContentFragment componentToUse="figcaption" classes="main-content">
                    {bestOfTheBestTeaser.metadata('htmlContent').value()}
                  </ContentFragment>
                  <img
                    src={getFullImageUrl(bestOfTheBestTeaser.metadata('teaserPhotoUrl').value(), 700)}
                    alt={bestOfTheBestTeaser.metadata('teaserPhotoTitle').value()}
                    className="w-100 h-100"
                  />
                </figure>
              )}
              {isBestOfTheBestPage && (
                <OnScreenContentWrapper className="animated-main-content text-start">
                  <ContentFragment classes="main-content">
                    {bestOfTheBestDescription.metadata('htmlContent').value()}
                  </ContentFragment>
                </OnScreenContentWrapper>
              )}
              {!isBestOfTheBestPage && featuredVideoImageGallery && (
                <PhotoflipperVideoImageTopRated
                  vehicleGallery={featuredVideoImageGallery}
                  fullImageSize={fullImageSizeFromMetadata}
                  animatedHover
                  isPreHovered={isMobile}
                />
              )}
              {!isBestOfTheBestPage && (
                <OnScreenContentWrapper className="animated-main-content text-start mt-3_5">
                  <MainContent entries={mainEntries} isMobile={isMobile} />
                </OnScreenContentWrapper>
              )}
              <div className="d-md-flex mx-auto justify-content-center" style={{ maxWidth: '600px' }}>
                {!showBestofTheBestTeaser && (
                  <AnimatedHoverLinkButton
                    linkUrl={coreLink}
                    linkText={coreLinkText}
                    wrapperClass="my-1_5 my-md-2_5"
                    isPreHovered={isMobile}
                  />
                )}
                {hasInlineAds && !isMobile && (
                  <FeatureFlag name="enableEtrRankingsAd">
                    <div className="align-top my-1_5 my-md-2_5 ms-md-3_5">
                      <AnimatedHoverLinkButton
                        linkUrl={CATEGORY_WINNER_CLICK_TAG[type][isBestOfTheBest]}
                        linkText={
                          <span>
                            {inlineAdsCtaCopy}
                            <i className="icon-open_in_new medium my-0_25 mx-0_25" aria-hidden />
                          </span>
                        }
                        wrapperClass=""
                        isPreHovered={isMobile}
                        target="_blank"
                        rel="noopener noreferrer"
                        dataTrackingId="view_content"
                        dataTrackingValue={CATEGORY_WINNER_CLICK_TAG[type][isBestOfTheBest]}
                        adUnit={
                          <AdUnit
                            {...ETR_ADS_PROPS[isMobile]}
                            position={'1'}
                            renderWhenViewable={false}
                            showLabel={false}
                          />
                        }
                      />
                      <a
                        href={CATEGORY_WINNER_CLICK_TAG[type][isBestOfTheBest]}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-tracking-id="view_content"
                        data-tracking-value={CATEGORY_WINNER_CLICK_TAG[type][isBestOfTheBest]}
                        className="d-block"
                      >
                        <div className="text-blue-50 d-block pos-r">{inlineAdsCtaClickOutUrl}</div>
                      </a>
                    </div>
                  </FeatureFlag>
                )}
              </div>

              {isBestOfTheBestPage && featuredVideoImageGallery && (
                <PhotoflipperVideoImageTopRated
                  vehicleGallery={featuredVideoImageGallery}
                  fullImageSize={fullImageSizeFromMetadata}
                  animatedHover
                  isPreHovered={isMobile}
                  thumbnailPhotoClass="rounded-16"
                />
              )}
            </section>

            {featuredRoadtestGallery && (
              <PhotoflipperVideoImageTopRated
                wrapperClass="my-n2_5 my-md-n3_5"
                vehicleGallery={featuredRoadtestGallery}
                fullImageSize={fullImageSizeFromMetadata}
                isCenterMode
                showMeatballs={false}
                centerPadding="15%"
                slickSlideClassName="road-test-gallery"
                isPreHovered={isMobile}
              />
            )}

            {vehicleGallery && (
              <PhotoflipperImageTopRated vehicleGallery={vehicleGallery} fullImageSize={fullImageSizeFromMetadata} />
            )}
          </article>
        </RevealOnIntersect>
        {hasInlineAds && !isMobile && (
          <FeatureFlag name="enableEtrRankingsAd">
            <RevealOnIntersect>
              <div className="leaderboard" style={{ height: '90px', marginTop: '52px' }}>
                <LeaderboardNative
                  wiredBreakpoints={{ md: true, lg: true, xl: true }}
                  noRefresh
                  refreshZones={null}
                  showLabel={false}
                  position={'1'}
                  renderWhenViewable
                />
              </div>
            </RevealOnIntersect>
          </FeatureFlag>
        )}
        {mainEntryData.finalists && (
          <RevealOnIntersect threshold={isMobile ? 0 : 0.1}>
            <Finalists
              entry={mainEntryData.finalists}
              titleLabel={FINALISTS_TITLE}
              isMobile={isMobile}
              isElectricPage={isElectricPage}
            />
          </RevealOnIntersect>
        )}
        <RevealOnIntersect threshold={isMobile ? 0 : 0.1}>
          <WinnerTilesNavArticle
            winnerVehicles={winnersEntries}
            year={year}
            winnerTitle={winnerTitle}
            title={title}
            isMobile={isMobile}
          />
        </RevealOnIntersect>
      </section>

      <RevealOnIntersect threshold={isMobile ? 0 : 0.1}>
        <section className="article-wrapper px-1 px-lg-0 mx-auto pos-r">
          {otherYearLinks.length ? <OtherYears otherYearLinks={otherYearLinks} year={year} /> : null}
          <Share
            url={canonical}
            title={title}
            labelClassName="d-none"
            containerClassName="d-block text-center py-3"
            iconClassName={classnames(
              'heading-4 mx-1 p-0',
              topRatedHelpers.swapTextColor('text-white', year, 'text-primary-darker')
            )}
          />
          {permissions && (
            <TopRatedAwardsAccoladesUI
              title={permissions.metadata('title').value()}
              content={permissions.metadata('content').value()}
              mailTo={permissions.metadata('mailTo').value()}
              email={permissions.metadata('email').value()}
              anchorClassName="text-white"
            />
          )}
        </section>
      </RevealOnIntersect>
    </main>
  );
}

TopRatedArticleUI.propTypes = {
  content: CmsEntities.Content.isRequired,
  year: PropTypes.string.isRequired,
  pathname: PropTypes.string,
  isMobile: PropTypes.bool,
  isDefaultYear: PropTypes.bool,
  type: PropTypes.string,
  themeContent: CmsEntities.Content,
};

export const TopRatedArticle = rendererTopRatedArticle(TopRatedArticleUI);
